import { useEffect, useState } from 'react';
import { Select, SelectItem, Text } from '@tremor/react';
import { api } from '../lib/api';
import type { XeraPreset, XeraPresetsResponse } from '../types';

interface PresetSelectorProps {
  value: string;
  onChange: (presetId: string, preset: XeraPreset | null) => void;
  disabled?: boolean;
  className?: string;
}

export function PresetSelector({ value, onChange, disabled, className }: PresetSelectorProps) {
  const [presets, setPresets] = useState<XeraPreset[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchPresets = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const response: XeraPresetsResponse = await api.getPresets();
        setPresets(response.presets);

        // If no value selected and there's a default, select it
        if (!value && response.defaultPresetId) {
          const defaultPreset = response.presets.find(p => p.id === response.defaultPresetId);
          if (defaultPreset) {
            onChange(response.defaultPresetId, defaultPreset);
          }
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load presets');
      } finally {
        setIsLoading(false);
      }
    };

    fetchPresets();
  }, []);

  const handleChange = (presetId: string) => {
    const preset = presets.find(p => p.id === presetId) || null;
    onChange(presetId, preset);
  };

  if (error) {
    return (
      <div className="bg-red-950/30 border border-red-900/50 rounded p-2">
        <Text className="text-red-400 text-sm">{error}</Text>
      </div>
    );
  }

  return (
    <div className={className}>
      <Select
        value={value}
        onValueChange={handleChange}
        disabled={disabled || isLoading}
        placeholder={isLoading ? 'Loading presets...' : 'Select data source...'}
        className="bg-gray-800 border-gray-700"
      >
        {presets.map((preset) => (
          <SelectItem key={preset.id} value={preset.id}>
            {preset.name}{preset.environment === 'production' ? ' [PROD]' : ''}
          </SelectItem>
        ))}
      </Select>

      {/* Show selected preset details */}
      {value && !isLoading && (() => {
        const selectedPreset = presets.find(p => p.id === value);
        if (!selectedPreset) return null;
        const isViewBased = selectedPreset.viewId !== undefined && selectedPreset.viewId !== null;
        return (
          <div className="mt-2 text-xs text-gray-500 space-y-0.5">
            <div>Project: {selectedPreset.projectId} | {isViewBased ? `View: ${selectedPreset.viewId}` : `Folder: ${selectedPreset.folderId}`}</div>
            <div>PDF Field: {selectedPreset.pdfField} | File Type Field: {selectedPreset.fileTypeField}</div>
            {selectedPreset.storageProvider && (
              <div>Storage: {selectedPreset.storageProvider}{selectedPreset.oneDriveFolder ? ` | Path: ${selectedPreset.oneDriveFolder}` : ''}</div>
            )}
          </div>
        );
      })()}
    </div>
  );
}
